class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this._filterState = { brands: {}, categories: {} };
    this._checkedState = {};

    this.setupEventListeners();
  }

  setupEventListeners() {

    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.querySelectorAll('.predictive-search__view-more').forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();

        let _id = button.dataset.toggle;

        document.querySelector(`#predictive-search__${_id}`).removeAttribute('data-less');
        document.querySelector(`#predictive-search__${_id}`).setAttribute('data-more', '');
      });
    });

    let _searchOffset = `${document.querySelector('.header').offsetHeight}px`;
    document.querySelector(':root').style.setProperty('--search-offset', _searchOffset);  
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      // this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      // Show clear button
      this.querySelector('.reset__button').classList.remove('hidden');
      this.querySelector('.search__button').classList.add('hidden');
      document.querySelector('.header__overlay').classList.add('active');
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.querySelector('.reset__button').classList.add('hidden');
      document.querySelector('.header__overlay').classList.remove('active');
      this.querySelector('.search__button').classList.remove('hidden');
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  isDesktop() {
    return window.matchMedia("(min-width: 1031px)").matches;
  }

  onFocusOut(event) {

    if (!this.isDesktop()) return;

    if (this.contains(event.relatedTarget)) return;
    this.close();
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  generateQuery(searchTerm) {

    document.querySelectorAll('.predictive-search--header input[type="checkbox"]').forEach((checkbox) => {
      this._checkedState[checkbox.id] = checkbox.checked;
    });

    const params = new URLSearchParams();

    Object.entries(this._checkedState)
      .filter(([id, checked]) => checked)
      .forEach(([id]) => {
        const [type, value] = id.split('__');
        if (value) params.append(type, value); // skip empty values
      });

    const filterParams = params.toString();

    const baseUrl = `https://search.shopbox.ai/api/v1/search?q=${encodeURIComponent(searchTerm)}&from=0&size=20&cid=shopify95376245071&in_stock=true&uuid=${JSON.parse(localStorage.getItem("svelte-app-v0.1.304")).uuid}`;

    const url = filterParams ? `${baseUrl}&${filterParams}` : baseUrl;

    return [url];
  }

  cleanTitle(term) {
    let _term = term.replace(/-/g, ' ');
    _term = _term.replace(/_/g, ' ');
    return _term.charAt(0).toUpperCase() + _term.slice(1);
  }

  normalizeApiPrices(prices) {
    const normalized = {};
    for (const [code, value] of Object.entries(prices)) {
      normalized[code.toUpperCase()] = value;
    }
    return normalized;
  }

  formatApiPrice(pricesByCurrency, targetCurrency, options = {}) {
    if (!pricesByCurrency || !targetCurrency) return '';

    const currencyCode = targetCurrency.toLowerCase();
    const amount = pricesByCurrency[currencyCode];

    if (amount == null) {
      // Fallback if that currency isn't in the response
      return '';
    }

    const locale =
      options.locale ||
      (window.themeLocalization && window.themeLocalization.locale) ||
      'en';

    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      // Options you might want to adjust:
      minimumFractionDigits:
        options.minimumFractionDigits != null ? options.minimumFractionDigits : 2,
      maximumFractionDigits:
        options.maximumFractionDigits != null ? options.maximumFractionDigits : 2
    });

    return formatter.format(amount);
  }

  formatTitle(product) {

    let _title = product.title.toLowerCase();
    let _brand = product.brand.toLowerCase();
    
    const titleWithoutBrand = _title.replace(_brand, '').trim();

    return titleWithoutBrand.length ? titleWithoutBrand : product.title;

  }

  formatBrand(product) {

    return product.brand.toLowerCase() || '';

  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    let _customerCurrency = window.Shopify.currency.active;

    const [_query] = this.generateQuery(searchTerm);

    fetch(_query, {
      headers: {
        "SB-TOKEN": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjE3NzkzNDcwODksImNsaWVudF9pZCI6InNob3BpZnk5NTM3NjI0NTA3MSIsInN1YiI6Ijc5YWEzY2EyMmY3NTQzMDQ4ZDI0OTMyNDU5MjNlYjE1IiwidXNlcl9pZCI6ImU4ZjgyMDhmZmZkMzQ4ZTVhZTBjYzIyN2JkOWY3Yzg3Iiwid2Vic2l0ZSI6IiIsIm5hbWUiOiJzZWFyY2giLCJhcGkiOnRydWV9.dMen3KX8vCewdCkr8buX9F2CVkQIAOik8NgxL3SgFKg",
      },
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.json();
      })
      .then((text) => {
        const hits = text.responses[1]?.hits?.hits || [];
        const products = hits.map((p) => p._source).filter(Boolean);
        const approved_tags = ['breathable', 'insulated', 'merino_wool', 'natural_materials', 'sku', 'waterproof', 'windproof'];


        const availableFilters = text.responses[1]?.aggregations.count_of_brands.buckets.sort((a, b) => a.key.localeCompare(b.key)) ?? {};
        const availableCategories = text.responses[1]?.aggregations.count_of_top_category.buckets.sort((a, b) => a.key.localeCompare(b.key)) ?? {};
        availableFilters.forEach((bucket) => {
          if (!this._filterState.brands) this._filterState.brands = {};
          if (bucket.key === '') return; // skip empty categories
          this._filterState.brands[bucket.key] = {
            value: bucket,
            checked: this._checkedState[`brands__${bucket.key}`] ?? false,
          };
        });

        availableCategories.forEach((bucket) => {
          if (!this._filterState.categories) this._filterState.categories = {};

          if (bucket.key === '') return; // skip empty categories
          if (bucket.key.includes('>')) return; // skip null categories

          this._filterState.categories[bucket.key] = {
            value: bucket,
            checked: this._checkedState[`categories__${bucket.key}`] ?? false,
          };
        });

        // --- Render Top Categories ---
        const topCategories = Object.entries(this._filterState.categories ?? {}).map(([id, { value, checked }]) => `<div class="predictive-search__category">
          <input type="checkbox" id="categories__${value.key}" value="${value.key}" name="categories__${value.key}" ${checked ? 'checked' : ''}>  
          <label for="categories__${value.key}" class="body-s">${this.cleanTitle(value.key)} <span class="category_count">${value.doc_count}</span></label>
          </div>
        `).join('');


        // --- Render ---
        const resultFilters = Object.entries(this._filterState.brands ?? {}).map(([id, { value, checked }]) => `<div class="predictive-search__category">
          <input type="checkbox" id="brands__${value.key}" value="${value.key}" name="brands__${value.key}" ${checked ? 'checked' : ''}>
          <label for="brands__${value.key}" class="body-s">${this.cleanTitle(value.key)} <span class="category_count">${value.doc_count}</span></label>
          </div>
        `).join('');

        const promoCardMap = window.searchPromo ? Object.values(window.searchPromo).reduce((map, card) => {
          map[card.index] = card;
          return map;
        }, {}) : {};

        const resultsHtml = `
          <div id="predictive-search-list">
            <ul class="predictive-search__results-list" role="listbox">
              ${products.map((p, i) => `
                ${promoCardMap[i] ? `
                  <li class="predictive-search__item predictive-search__item--promo" role="option">
                    <div class="predictive-search__promo-card">
                      ${promoCardMap[i].link ? `<a href="${promoCardMap[i].link}">` : ''}
                      <p class="body-s search__promo--${promoCardMap[i].colour}">${promoCardMap[i].text}</p>
                      ${promoCardMap[i].image ? `<img src="${promoCardMap[i].image}" alt="${promoCardMap[i].text}" width="266" loading="lazy" />` : ''}
                      ${promoCardMap[i].link ? `</a>` : ''}
                    </div>
                  </li>
                ` : ''}
                <li class="predictive-search__item" role="option">
                  ${this.generateTags(p.category_list)}
                  <a href="/products/${p.handle}?collection=search&position=${i}&sbclid=productCard" class="predictive-search__item-link">
                    ${p.image ? `<div class="predictive-search__item-image">
                      <img src="${p.image}" alt="${p.title}" width="266" loading="lazy" />
                    </div>` : ''}
                    <div class="predictive-search__item-content">
                      <div class="predictive-search__item-rating">
                        <span class="lipscore-rating-small"
                          data-ls-readonly="true"
                          data-ls-product-name="${p.title}"
                          data-ls-brand="${p.brand}"
                          data-ls-product-id="${p.id}"
                          data-ls-product-url="${p.url}"
                          data-ls-sku="${p.shopify_sku}"
                          data-ls-image-url="${p.image}"
                          data-ls-price="${p.price[_customerCurrency]}"
                          data-ls-price-currency="${_customerCurrency}"></span>
                      </div>
                      <p class="predictive-search__item-category body-xs">${this.formatBrand(p)}</p>
                      <p class="predictive-search__item-title body-s">${this.formatTitle(p)}</p>
                      <div class="predictive-search__item-pricing ${p.on_sale ? `on-sale` : ''}">
                        ${p.on_sale ? `<p class="predictive-search__item-price price--on-sale">${this.formatApiPrice(p.price, _customerCurrency)}</p>${p.regular_price[_customerCurrency.toLowerCase()] ? `<p class="predictive-search__item-price">${this.formatApiPrice(p.regular_price, _customerCurrency)}</p>` : ''}` : `<p class="predictive-search__item-price">${this.formatApiPrice(p.price, _customerCurrency)}</p>`}
                        
                        
                      </div>
                    </div>
                  </a>
                </li>
              `).join('')}
            </ul>
          </div>
        `;

        this.cachedResults[queryKey] = resultsHtml;

        const updateResults = () => {
          this.querySelector('#predictive-search__list').innerHTML = resultsHtml;
          this.querySelector('#predictive-search__filters').innerHTML = resultFilters;
          this.querySelector('#predictive-search__categories').innerHTML = topCategories;
          this.renderSearchResults();
          this.qSelectors();
        };

        if (document.startViewTransition) {
          document.startViewTransition(updateResults);
        } else {
          updateResults();
        }
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  generateTags(tags) {
    if (!tags || !tags.length) return '';

    let count = 0;

    const approved_tags = ['breathable', 'insulated', 'merino_wool', 'natural_materials', 'sku', 'waterproof', 'windproof'];

    const filtered_tags = tags.filter(tag => {

      if (count >= 1) return false;

      if (approved_tags.includes(tag)) {
        count++;
        return tag;
      }

    });

    return filtered_tags.length ? `
      <div class="predictive-search__item-tags">
        ${filtered_tags.map(tag => `<span class="predictive-search__item-tag body-xs">${this.cleanTitle(tag)}</span>`).join('')}
      </div>
    ` : '';
  }

  qSelectors() {
    document.querySelectorAll('.predictive-search--header input[type="checkbox"]').forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        this.onChange();
      });
  });
}

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults() {
    // this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    // this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    document.querySelector('body').classList.add('overflow-hidden');
    document.querySelector('html').classList.add('overflow-hidden');
    let _searchOffset = `${document.querySelector('.header').offsetHeight}px`;
    document.querySelector(':root').style.setProperty('--search-offset', _searchOffset);  
    this.querySelector('.reset__button').classList.remove('hidden');
    this.querySelector('.search__button').classList.add('hidden');
    document.querySelector('.header__overlay').classList.add('active');
    document.querySelector('#gorgias-chat-container').classList.add('hidden');
    document.querySelector('shopbox-app-v2').classList.add('hidden');
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    document.querySelector('body').classList.remove('overflow-hidden');
    document.querySelector('html').classList.remove('overflow-hidden');
    this.querySelector('.reset__button').classList.add('hidden');
    this.querySelector('.search__button').classList.remove('hidden');
    document.querySelector('.header__overlay').classList.remove('active');
    document.querySelector('#gorgias-chat-container').classList.remove('hidden');
    document.querySelector('shopbox-app-v2').classList.remove('hidden');
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this._filterState = { brands: {}, categories: {} };
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
